import { Accordion, Box, Button, Group, Modal, ScrollArea, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
/**
 * VersionHistoryExpander — T28 reusable component.
 *
 * Renders a Mantine Accordion listing prior versions of any versioned resource.
 * Each panel shows: full content (monospace, scrollable), version number, date,
 * author, and a "Revert to this version" button.
 *
 * Props:
 *   versions    — array of version objects to display
 *   onRevert    — called with the version number when the user confirms revert
 *   getPreview? — optional function returning a display label for a version's content
 */
import { useState } from 'react';

export interface VersionEntry {
  version: number;
  content: string;
  createdAt: string;
  createdBy?: string | null;
}

export interface VersionHistoryExpanderProps {
  versions: VersionEntry[];
  onRevert: (version: number) => Promise<void>;
  getPreview?: (v: VersionEntry) => string;
}

export function VersionHistoryExpander({
  versions,
  onRevert,
  getPreview,
}: VersionHistoryExpanderProps) {
  const [revertTarget, setRevertTarget] = useState<number | null>(null);
  const [reverting, setReverting] = useState(false);

  async function handleConfirmRevert() {
    if (revertTarget === null) return;
    setReverting(true);
    try {
      await onRevert(revertTarget);
      notifications.show({
        title: 'Reverted',
        message: `Reverted to version ${revertTarget}`,
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to revert version',
        color: 'red',
      });
    } finally {
      setReverting(false);
      setRevertTarget(null);
    }
  }

  if (versions.length === 0) {
    return (
      <Box data-testid="version-history-expander">
        <Text size="sm" c="dimmed">
          No previous versions.
        </Text>
      </Box>
    );
  }

  return (
    <Box data-testid="version-history-expander">
      <Accordion variant="separated">
        {versions.map((v) => {
          const label = getPreview
            ? getPreview(v)
            : `Version ${v.version} — ${new Date(v.createdAt).toLocaleString()} by ${v.createdBy ?? 'system'}`;

          return (
            <Accordion.Item key={v.version} value={String(v.version)}>
              <Accordion.Control data-testid="version-panel-trigger">
                <Text size="sm">{label}</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <ScrollArea h={400} mb="sm">
                  <Box
                    component="pre"
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      whiteSpace: 'pre-wrap',
                      margin: 0,
                    }}
                  >
                    {v.content}
                  </Box>
                </ScrollArea>
                <Button
                  size="xs"
                  variant="outline"
                  color="orange"
                  onClick={() => setRevertTarget(v.version)}
                  data-testid="revert-version-btn"
                >
                  Revert to this version
                </Button>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>

      {/* Confirmation modal */}
      <Modal
        opened={revertTarget !== null}
        onClose={() => setRevertTarget(null)}
        title="Revert version"
        data-testid="revert-confirm-dialog"
      >
        <Text size="sm" mb="lg">
          Are you sure you want to revert to version {revertTarget}? This will create a new current
          version with the content from version {revertTarget}.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setRevertTarget(null)} disabled={reverting}>
            Cancel
          </Button>
          <Button
            color="orange"
            loading={reverting}
            onClick={() => void handleConfirmRevert()}
            data-testid="revert-confirm-btn"
          >
            Revert
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}
